import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, Truck, ShieldCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LangContext";
import { faqList } from "@/lib/i18n";

const INTRO = {
  fr: {
    eyebrow: "Centre d'aide",
    lead:
      "Bienvenue dans le centre d'aide myFLOVEA. Vous trouverez ci-dessous les réponses aux questions les plus fréquentes sur la livraison, le paiement, la garantie, l'entretien de nos produits (Coussin Massant Chauffant, Sphère Duo Massage) et l'exercice de vos droits en tant que consommateur européen.",
    lead2:
      "Notre équipe support basée en France répond sous 24 heures ouvrées, par email et sur les canaux sociaux. Si vous ne trouvez pas la réponse à votre question, n'hésitez pas à nous écrire — nous sommes là pour vous accompagner à chaque étape, de la commande à l'après-vente.",
    catsTitle: "Sujets couverts par cette FAQ",
    cats: [
      { icon: Truck, label: "Livraison & suivi de commande" },
      { icon: ShieldCheck, label: "Garantie 2 ans & SAV" },
      { icon: Mail, label: "Retours 30 jours & remboursement" },
      { icon: MessageCircle, label: "Paiement sécurisé & moyens acceptés" },
    ],
    ctaTitle: "Vous n'avez pas trouvé votre réponse ?",
    ctaText:
      "Notre service client répond en moins de 24h ouvrées à toutes vos questions. Contactez-nous directement — un humain, basé en France, vous répondra personnellement.",
    ctaBtn: "Contacter le service client",
    trust:
      "Service client 100 % francophone · Support par email et messagerie · Basé à Paris (EM GROUP SASU · SIRET 100 603 026 00012)",
  },
  en: {
    eyebrow: "Help center",
    lead:
      "Welcome to the myFLOVEA help center. Below you'll find answers to the most frequent questions about shipping, payment, warranty, product care (Heated Massage Cushion, Duo Massage Sphere) and how to exercise your rights as a European consumer.",
    lead2:
      "Our France-based support team replies within 24 business hours by email and on social channels. If you can't find your answer here, feel free to reach out — we're here at every step, from checkout to after-sales.",
    catsTitle: "Topics covered in this FAQ",
    cats: [
      { icon: Truck, label: "Shipping & order tracking" },
      { icon: ShieldCheck, label: "2-year warranty & after-sales" },
      { icon: Mail, label: "30-day returns & refunds" },
      { icon: MessageCircle, label: "Secure payment methods" },
    ],
    ctaTitle: "Didn't find your answer?",
    ctaText:
      "Our customer service replies within 24 business hours to every message. Contact us directly — a real human, based in France, will personally reply.",
    ctaBtn: "Contact customer service",
    trust:
      "100% French-speaking support · Email & messenger · Based in Paris (EM GROUP SASU · SIRET 100 603 026 00012)",
  },
};

const Faq = () => {
  const { t, lang } = useLang();
  const items = faqList[lang];
  const copy = INTRO[lang];

  // JSON-LD FAQPage schema — enables Google rich snippets
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  };

  return (
    <section
      data-testid="faq-page"
      className="max-w-3xl mx-auto px-6 sm:px-10 py-20 md:py-28"
    >
      <Seo
        lang={lang}
        path="/faq"
        title={lang === "fr" ? "FAQ — Questions fréquentes" : "FAQ — Frequently asked questions"}
        description={
          lang === "fr"
            ? "FAQ myFLOVEA : livraison rapide depuis l'Allemagne 3 à 7 jours ouvrés, retours 30 jours, garantie 2 ans, paiement sécurisé Stripe, différence entre Tissu et Velours du Coussin Massant Chauffant."
            : "myFLOVEA FAQ: fast delivery from Germany 3–7 business days, 30-day returns, 2-year warranty, secure Stripe payment, Fabric vs Velvet cushion differences."
        }
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="eyebrow mb-6">{copy.eyebrow}</div>
      <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight">
        {t.legal.faqTitle}
      </h1>

      <p className="mt-8 text-[17px] leading-relaxed text-[#4a4a4a] max-w-2xl">
        {copy.lead}
      </p>
      <p className="mt-4 text-[15px] leading-relaxed text-[#4a4a4a] max-w-2xl">
        {copy.lead2}
      </p>

      {/* Categories band */}
      <div className="mt-10 border-t border-b border-[#dcd7ce] py-6">
        <div className="eyebrow mb-4 text-[#8B6343]">{copy.catsTitle}</div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {copy.cats.map((c, i) => {
            const Icon = c.icon;
            return (
              <li
                key={i}
                data-testid={`faq-category-${i}`}
                className="flex items-center gap-3 text-[14px] text-[#3D2B1F]"
              >
                <Icon size={16} strokeWidth={1.5} className="text-[#8B6343] shrink-0" />
                {c.label}
              </li>
            );
          })}
        </ul>
      </div>

      <Accordion type="single" collapsible className="mt-10">
        {items.map((it, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            data-testid={`faq-item-${i}`}
            className="border-b border-[#dcd7ce]"
          >
            <AccordionTrigger className="text-left font-serif-display text-xl md:text-2xl py-6 hover:no-underline">
              {it.q}
            </AccordionTrigger>
            <AccordionContent className="text-[16px] text-[#4a4a4a] leading-relaxed pb-6">
              {it.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* CTA block */}
      <div
        data-testid="faq-contact-cta"
        className="mt-16 bg-[#F5EFE6] border border-[#dcd7ce] rounded-md px-8 py-10 text-center"
      >
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A]">
          {copy.ctaTitle}
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-[#4a4a4a] max-w-xl mx-auto">
          {copy.ctaText}
        </p>
        <Link
          to="/contact"
          data-testid="faq-contact-link"
          className="mt-6 inline-flex items-center gap-2 bg-[#3D2B1F] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase rounded-md hover:bg-[#1A1A1A] transition-colors"
        >
          <Mail size={14} />
          {copy.ctaBtn}
        </Link>
        <p className="mt-6 text-xs text-[#8a8378] leading-relaxed">{copy.trust}</p>
      </div>
    </section>
  );
};

export default Faq;
